import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    Tag,
    notification,
    Row,
    Col,
    Statistic,
    Avatar
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    SearchOutlined,
    ShoppingCartOutlined,
    EyeOutlined,
    DollarCircleOutlined
} from '@ant-design/icons';
import { purchaseService } from '../../services/purchaseService';
import PurchaseForm from './PurchaseForm';

const { Column } = Table;
const { Search } = Input;

const PurchaseList = () => {
    const navigate = useNavigate();
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);

    const fetchPurchases = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const response = await purchaseService.getAllPurchases({
                page,
                limit: pagination.pageSize,
                search
            });

            setPurchases(Array.isArray(response?.data) ? response.data : []);

            setPagination(prev => ({
                ...prev,
                current: response?.pagination?.page || 1,
                total: response?.pagination?.total || 0
            }));
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch purchase invoices'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchases();
    }, []);

    const handleTableChange = (pagination) => {
        fetchPurchases(pagination.current, searchText);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchPurchases(1, value);
    };

    const handleAdd = () => {
        setSelectedPurchase(null);
        setIsDrawerVisible(true);
    };

    const handleEdit = (purchase) => {
        setSelectedPurchase(purchase);
        setIsDrawerVisible(true);
    };

    const handleView = (id) => {
        navigate(`/purchases/${id}`);
    };

    const handleDrawerClose = () => {
        setIsDrawerVisible(false);
        setSelectedPurchase(null);
    };

    const handleSuccess = () => {
        handleDrawerClose();
        fetchPurchases(pagination.current, searchText);
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space>
                                <Avatar size="large" icon={<ShoppingCartOutlined />} style={{ backgroundColor: '#1890ff' }} />
                                <div>
                                    <h3 style={{ margin: 0 }}>Purchase Invoices</h3>
                                    <p style={{ margin: 0, color: '#8c8c8c' }}>Manage vendor purchases</p>
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            <Space>
                                <Search
                                    placeholder="Search by invoice number or vendor"
                                    allowClear
                                    enterButton={<SearchOutlined />}
                                    size="large"
                                    style={{ width: 300 }}
                                    onSearch={handleSearch}
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    size="large"
                                    onClick={handleAdd}
                                >
                                    New Purchase
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Table
                        dataSource={purchases}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} purchases`
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                    >
                        <Column
                            title="Invoice Number"
                            dataIndex="invoiceNumber"
                            key="invoiceNumber"
                            render={(text) => <strong>{text}</strong>}
                        />
                        <Column
                            title="Vendor"
                            dataIndex={['vendor', 'vendorName']}
                            key="vendorName"
                        />
                        <Column
                            title="Date"
                            dataIndex="invoiceDate"
                            key="invoiceDate"
                            render={(date) => new Date(date).toLocaleDateString()}
                        />
                        <Column
                            title="Items"
                            dataIndex="items"
                            key="items"
                            render={(items) => items?.length || 0}
                        />
                        <Column
                            title="Total Before Tax"
                            dataIndex="totalBeforeTax"
                            key="totalBeforeTax"
                            render={(amount) => `₹${parseFloat(amount || 0).toFixed(2)}`}
                        />
                        <Column
                            title="Tax"
                            dataIndex="totalTax"
                            key="totalTax"
                            render={(amount) => `₹${parseFloat(amount || 0).toFixed(2)}`}
                        />
                        <Column
                            title="Total"
                            dataIndex="totalAfterTax"
                            key="totalAfterTax"
                            render={(amount) => (
                                <strong style={{ color: '#52c41a' }}>
                                    ₹{parseFloat(amount || 0).toFixed(2)}
                                </strong>
                            )}
                        />
                        <Column
                            title="Actions"
                            key="actions"
                            fixed="right"
                            width={150}
                            render={(_, record) => (
                                <Space>
                                    <Button
                                        type="link"
                                        icon={<EyeOutlined />}
                                        onClick={() => handleView(record.id)}
                                    >
                                        View
                                    </Button>
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => handleEdit(record)}
                                    >
                                        Edit
                                    </Button>
                                </Space>
                            )}
                        />
                    </Table>
                </Space>
            </Card>

            <Drawer
                title={selectedPurchase ? 'Edit Purchase Invoice' : 'Create Purchase Invoice'}
                width={720}
                onClose={handleDrawerClose}
                open={isDrawerVisible}
                destroyOnClose
            >
                <PurchaseForm
                    purchase={selectedPurchase}
                    onSuccess={handleSuccess}
                    onCancel={handleDrawerClose}
                />
            </Drawer>
        </Space>
    );
};

export default PurchaseList;

